import React, { FC, useMemo } from 'react';
import { FiChevronLeft, FiChevronRight, FiExternalLink } from 'react-icons/fi';

import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';
import routes from '../../../../constants/routes';

import { FormulaBaseModel } from '../../../../models/formula.model';

import envConfig from '../../../../services/env-config';

import { SearchableSelectType } from '../../../inputs/searchableSelect';
import MapElementList from '../MapElementList';

interface Step2FieldMappingProps {
    selectedMessages: FormulaBaseModel[];
    selectedMessageForPreview: FormulaBaseModel;
    currentPreviewIndex: number;
    onPrevMessage: () => void;
    onNextMessage: () => void;
    mapOptions: SearchableSelectType[];
    showMappingErrors?: boolean;
}

const Step2FieldMapping: FC<Step2FieldMappingProps> = ({
    selectedMessages,
    selectedMessageForPreview,
    currentPreviewIndex,
    onPrevMessage,
    onNextMessage,
    mapOptions,
    showMappingErrors,
}) => {
    const messageUrl = useMemo(() => {
        const path = routes.formulaView(
            selectedMessageForPreview.status === 'Approved'
                ? 'Live'
                : selectedMessageForPreview.status,
            selectedMessageForPreview.id,
        );
        const base = envConfig.BASE_PATH.replace(/\/$/, '');
        return base + path;
    }, [selectedMessageForPreview]);

    const isFirst = currentPreviewIndex === 0;
    const isLast = currentPreviewIndex === selectedMessages.length - 1;
    const isSingle = selectedMessages.length === 1;

    return (
        <Container>
            <TopSection>
                <TopRow>
                    <InfoSection>
                        <InfoRow>
                            <Label>
                                Changing the email template will affect{' '}
                                <Count>
                                    {selectedMessages.length} message group
                                    {selectedMessages.length !== 1 ? 's' : ''}
                                </Count>
                                .
                            </Label>
                        </InfoRow>
                        <InfoText>
                            Assign the elements below to the corresponding
                            fields in the new template.
                        </InfoText>
                    </InfoSection>

                    <PreviewSection>
                        <Label>Currently previewing message content from</Label>
                        <MessageSelectorRow>
                            <NavButton
                                onClick={onPrevMessage}
                                disabled={isFirst || isSingle}
                                aria-label="Previous message"
                            >
                                <FiChevronLeft size={16} strokeWidth={2} />
                            </NavButton>
                            <MessageCounter>
                                {selectedMessageForPreview.name ||
                                    'Untitled Message'}
                                <MessageCountBadge>
                                    {currentPreviewIndex + 1} /{' '}
                                    {selectedMessages.length}
                                </MessageCountBadge>
                            </MessageCounter>
                            <NavButton
                                onClick={onNextMessage}
                                disabled={isLast || isSingle}
                                aria-label="Next message"
                            >
                                <FiChevronRight size={16} strokeWidth={2} />
                            </NavButton>
                            <IconButton
                                onClick={() =>
                                    window.open(messageUrl, '_blank')
                                }
                                aria-label="Open message in new tab"
                            >
                                <FiExternalLink size={18} />
                            </IconButton>
                        </MessageSelectorRow>
                    </PreviewSection>
                </TopRow>
            </TopSection>

            <MappingSection>
                <MapElementList
                    mapOptions={mapOptions}
                    selectedMessageForPreview={selectedMessageForPreview}
                    showMappingErrors={showMappingErrors}
                />
            </MappingSection>
        </Container>
    );
};

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
    height: '100%',
});

const TopSection = styled.div({
    display: 'flex',
    flexDirection: 'column',
});

const TopRow = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.white,
    gap: aampeTheme.spacing4(10),
    padding: aampeTheme.spacing4(3, 4, 4, 4),
});

const InfoSection = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
    flex: 1,
});

const InfoRow = styled.div({
    display: 'flex',
    alignItems: 'center',
});

const Label = styled.span({
    fontSize: aampeTheme.size.font.default,
    color: aampeTheme.colors.neutral09,
    fontWeight: aampeTheme.weights.heavy,
    lineHeight: aampeTheme.size.lineHeight.small,
});

const Count = styled.span({
    fontWeight: aampeTheme.weights.medium,
    color: aampeTheme.colors.neutral09,
});

const InfoText = styled.span({
    fontSize: aampeTheme.size.font.default,
    color: aampeTheme.colors.neutral09,
    fontWeight: aampeTheme.weights.light,
    lineHeight: aampeTheme.size.lineHeight.default,
});

const PreviewSection = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
    flex: 1,
    paddingLeft: aampeTheme.spacing4(4),
});

const MessageSelectorRow = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(1),
});

const NavButton = styled.button({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    background: 'none',
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    cursor: 'pointer',
    color: aampeTheme.colors.solidA01,
    transition: 'all 0.2s',
    flexShrink: 0,
    '&:hover:not(:disabled)': {
        backgroundColor: aampeTheme.colors.backgroundWG02,
        borderColor: aampeTheme.colors.solidA01,
    },
    '&:disabled': {
        color: aampeTheme.colors.bordersWG01,
        borderColor: aampeTheme.colors.interactiveWG01,
        cursor: 'not-allowed',
    },
});

const MessageCounter = styled.span({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.neutral09,
    fontWeight: aampeTheme.weights.light,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const MessageCountBadge = styled.span({
    fontSize: aampeTheme.size.font.textSmall,
    color: aampeTheme.colors.textAccessibleWG01,
    fontWeight: aampeTheme.weights.light,
    whiteSpace: 'nowrap',
    flexShrink: 0,
});

const IconButton = styled.button({
    background: 'none',
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    cursor: 'pointer',
    padding: aampeTheme.spacing4(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: aampeTheme.colors.solidA01,
    transition: 'all 0.2s',
    flexShrink: 0,
    '&:hover': {
        backgroundColor: aampeTheme.colors.backgroundWG02,
        borderColor: aampeTheme.colors.solidA01,
    },
});

const MappingSection = styled.div({
    flex: 1,
    overflow: 'auto',
});

export default Step2FieldMapping;
